import { type ReactNode } from "react";
import { useNavigate, type NavigateFunction } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { cn } from "@shared/lib/cn";
import { useUiStore } from "@shared/stores/uiStore";
import { SegmentedControl } from "@shared/ui/lists/SegmentedControl";
import { Button, type ButtonProps } from "@shared/ui/primitives/Button";

function normalizeHeaderTitle(title: ReactNode): ReactNode {
  if (typeof title === "string") {
    return title.replace(/\.\s*$/, "");
  }
  if (Array.isArray(title)) {
    return title.map((part, index) => <span key={index}>{normalizeHeaderTitle(part)}</span>);
  }
  return title;
}

function canNavigateBack(): boolean {
  if (typeof window === "undefined") return false;
  const idx = window.history.state?.idx;
  return typeof idx === "number" && idx > 0;
}

function navigateBackOrFallback(
  navigate: NavigateFunction,
  previousPath: string | null,
): void {
  if (canNavigateBack() && (previousPath === "/app/home" || previousPath === "/app/more")) {
    navigate(-1);
    return;
  }
  navigate("/app/home");
}

export interface HeroHeaderTab<T extends string> {
  value: T;
  label: string;
}

export interface HeroHeaderProps<T extends string = string> {
  eyebrow?: ReactNode;
  eyebrowSuffix?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  showBack?: boolean;
  leading?: ReactNode;
  backTo?: string;
  onBack?: () => void;
  backLabel?: string;
  backBehavior?: "contextual" | "parent";
  action?: ReactNode;
  tabs?: ReadonlyArray<HeroHeaderTab<T>>;
  activeTab?: T;
  onTabChange?: (value: T) => void;
  tabsLabel?: string;
  backgroundImage?: string;
  /** Pin the header to the top while the page scrolls. Default true; pages with
   *  their own scroll/layout (e.g. the home dashboard) can opt out. */
  sticky?: boolean;
  collapsible?: boolean;
  overlayClassName?: string;
  className?: string;
  contentClassName?: string;
  topRowClassName?: string;
  bodyClassName?: string;
  eyebrowClassName?: string;
  titleClassName?: string;
  subtitleClassName?: string;
  children?: ReactNode;
}

export interface HeroHeaderActionButtonProps extends Omit<
  ButtonProps,
  "children" | "size" | "aria-label"
> {
  icon: ReactNode;
  label: string;
}

export interface CompactHeaderProps {
  title: ReactNode;
  backTo?: string;
  onBack?: () => void;
  backLabel?: string;
  backBehavior?: "contextual" | "parent";
  action?: ReactNode;
  className?: string;
}

export function HeroHeaderActionButton({
  icon,
  label,
  variant = "gold",
  className,
  ...props
}: HeroHeaderActionButtonProps): JSX.Element {
  return (
    <Button
      {...props}
      aria-label={label}
      variant={variant}
      size="icon"
      className={cn(
        "h-10 w-10 rounded-full shadow-sm",
        variant === "ghost" &&
          "border border-white/12 bg-white/15 text-white hover:bg-white/22 backdrop-blur",
        variant === "gold" && "hover:opacity-95",
        className,
      )}
    >
      {icon}
    </Button>
  );
}

export function CompactHeader({
  title,
  backTo = "/app/home",
  onBack,
  backLabel = "Back",
  backBehavior = "contextual",
  action,
  className,
}: CompactHeaderProps): JSX.Element {
  const navigate = useNavigate();
  const previousPath = useUiStore((s) => s.previousPath);
  const displayTitle = normalizeHeaderTitle(title);
  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    if (backBehavior === "parent") {
      navigate(backTo);
      return;
    }
    navigateBackOrFallback(navigate, previousPath);
  };

  return (
    <header
      className={cn(
        "sticky top-0 z-30 overflow-hidden rounded-b-sm bg-primary-900 px-5 pt-safe-or-3 pb-3 backdrop-blur-xl backdrop-saturate-150",
        "[background:color-mix(in_oklch,var(--primary-900)_88%,transparent)]",
        "shadow-[inset_0_-1px_0_rgba(255,255,255,0.10),0_12px_30px_-18px_rgba(0,0,0,0.55)]",
        className,
      )}
    >
      <div className="grid min-h-9 grid-cols-[2.25rem_minmax(0,1fr)_auto] items-center gap-3">
        <Button
          type="button"
          onClick={handleBack}
          aria-label={backLabel}
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0 rounded-full border border-white/10 bg-white/12 text-white hover:bg-white/18"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={1.75} />
        </Button>
        <h1 className="min-w-0 truncate text-left font-display text-body font-semibold leading-6 text-white">
          {displayTitle}
        </h1>
        {action ? <div className="shrink-0">{action}</div> : <div aria-hidden className="w-0" />}
      </div>
    </header>
  );
}

export function HeroHeader<T extends string = string>({
  eyebrowSuffix,
  title,
  subtitle,
  showBack = true,
  leading,
  backTo = "/app/home",
  onBack,
  backLabel = "Back",
  backBehavior = "contextual",
  action,
  tabs,
  activeTab,
  onTabChange,
  tabsLabel,
  backgroundImage,
  sticky = true,
  collapsible = true,
  overlayClassName,
  className,
  contentClassName,
  topRowClassName,
  bodyClassName,
  eyebrowClassName,
  titleClassName,
  subtitleClassName,
  children,
}: HeroHeaderProps<T>): JSX.Element {
  const navigate = useNavigate();
  const headerCollapsed = useUiStore((s) => s.headerCollapsed);
  const previousPath = useUiStore((s) => s.previousPath);
  const compactHeaderEnabled = sticky && collapsible;
  const showCompactHeader = compactHeaderEnabled && headerCollapsed;
  const displayTitle = normalizeHeaderTitle(title);
  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    if (backBehavior === "parent") {
      navigate(backTo);
      return;
    }
    navigateBackOrFallback(navigate, previousPath);
  };
  const leadingContent =
    leading ??
    (showBack ? (
      <Button
        type="button"
        onClick={handleBack}
        aria-label={backLabel}
        variant="ghost"
        size="icon"
        className="h-10 w-10 shrink-0 rounded-full border border-white/10 bg-white/12 text-white hover:bg-white/18"
      >
        <ArrowLeft className="h-4 w-4" strokeWidth={1.75} />
      </Button>
    ) : null);

  return (
    <>
      <header
        className={cn(
          // Collapsible headers keep the full hero in normal flow and let a
          // separate compact bar handle the fixed chrome. Non-collapsible
          // headers retain the original sticky behavior.
          sticky && !collapsible ? "sticky top-0 z-30" : "relative",
          "overflow-hidden rounded-b-md bg-primary-900 px-5 pt-safe-or-6 pb-6 backdrop-blur-xl backdrop-saturate-150",
          "[background:color-mix(in_oklch,var(--primary-900)_76%,transparent)]",
          "shadow-[inset_0_-1px_0_rgba(255,255,255,0.14),0_12px_32px_-28px_rgba(0,0,0,0.55)]",
          className,
        )}
      >
        {backgroundImage ? (
          <div
            aria-hidden
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(\"${backgroundImage}\")` }}
          />
        ) : null}
        <div
          aria-hidden
          className={cn(
            "absolute inset-0 bg-gradient-to-b backdrop-blur-sm backdrop-saturate-150",
            !backgroundImage && "from-white/18 via-black/8 to-black/32",
            // Strong scrim over photo headers so the white title/eyebrow stay
            // legible on bright images (restored after a regression that dropped
            // it to from-black/18 — illegible over light photos).
            backgroundImage && "from-black/50 via-black/60 to-black/85",
            overlayClassName,
          )}
        />
        <div className={cn("relative", contentClassName)}>
          {leading ? (
            <div
              className={cn(
                "flex items-center",
                action ? "justify-between" : "justify-start",
                topRowClassName,
              )}
            >
              {leadingContent}
              {action}
            </div>
          ) : null}
          <div className={bodyClassName}>
            <div
              className={cn(
                leading
                  ? "mt-3 flex items-center gap-3"
                  : "mt-1 flex items-center gap-3",
              )}
            >
              {showBack && !leading ? leadingContent : null}
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <div className="min-w-0 flex-1">
                  {eyebrowSuffix ? (
                    <div
                      className={cn(
                        "flex flex-wrap items-center gap-2",
                        eyebrowClassName,
                      )}
                    >
                      {eyebrowSuffix}
                    </div>
                  ) : null}
                  <div className="flex flex-wrap items-center gap-2">
                    <h1
                      className={cn(
                        "display-serif text-display-lg leading-tight text-white",
                        titleClassName,
                      )}
                    >
                      {displayTitle}
                    </h1>
                  </div>
                  {subtitle ? (
                    <p
                      className={cn(
                        "mt-1 max-w-md text-body-sm leading-snug text-white/70",
                        subtitleClassName,
                      )}
                    >
                      {subtitle}
                    </p>
                  ) : null}
                </div>
                {action && !leading ? (
                  <div className="shrink-0">{action}</div>
                ) : null}
              </div>
            </div>
            {children}
            {tabs?.length && activeTab && onTabChange ? (
              <SegmentedControl
                options={tabs}
                value={activeTab}
                onChange={onTabChange}
                ariaLabel={tabsLabel}
                fullWidth
                tone="inverse"
                className="mt-6"
              />
            ) : null}
          </div>
        </div>
      </header>

      {compactHeaderEnabled ? (
        <div
          aria-hidden={!showCompactHeader}
          className={cn(
            "fixed inset-x-0 top-0 z-40 overflow-hidden rounded-b-sm bg-primary-900 px-5 pt-safe-or-3 pb-3 backdrop-blur-xl backdrop-saturate-150",
            "[background:color-mix(in_oklch,var(--primary-900)_88%,transparent)]",
            "shadow-[inset_0_-1px_0_rgba(255,255,255,0.10),0_12px_30px_-18px_rgba(0,0,0,0.55)]",
            "transform-gpu transition-[opacity,transform,visibility] duration-hero ease-decel [backface-visibility:hidden] [will-change:transform,opacity]",
            showCompactHeader
              ? "visible translate-y-0 opacity-100"
              : "invisible pointer-events-none -translate-y-full opacity-0",
          )}
        >
          <div className="grid min-h-9 grid-cols-[2.25rem_minmax(0,1fr)_auto] items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center">
              {leading ? leadingContent : showBack ? leadingContent : null}
            </div>
            {leading ? (
              <>
                <div className="min-w-0" />
                {action ? <div className="shrink-0">{action}</div> : null}
              </>
            ) : (
              <>
                <h1 className="min-w-0 truncate text-left font-display text-body font-semibold leading-6 text-white">
                  {displayTitle}
                </h1>
                {action ? <div className="shrink-0">{action}</div> : null}
              </>
            )}
          </div>
        </div>
      ) : null}
    </>
  );
}
